import { useState } from 'react'
import { Button, message, Modal, Radio, Space } from 'antd'
import { Text } from '@weareberyl/design-system'

import { CurrencyInput } from '~/components/shared/numberInputs'
import {
  SubscriptionStatus,
  useCancelUserSubscriptionMutation,
  type UserSubscriptionListQuery_user_User_user_subscriptions_UserSubscription as UserSubscription,
} from '~/gql/generated/graphql'

import { useRefetchSubscriptions } from './hooks'

const isLive = ({ status }: UserSubscription) =>
  [SubscriptionStatus.active, SubscriptionStatus.past_due].includes(status)

// The API refunds nothing unless it is asked to
type RefundChoice = 'none' | 'prorate' | 'custom'

type RefundOptionsProps = {
  currency: string | undefined
  refundChoice: RefundChoice
  onRefundChoiceChange: (choice: RefundChoice) => void
  refund: number | null
  onRefundChange: (refund: number | null) => void
}

const RefundOptions = ({
  currency,
  refundChoice,
  onRefundChoiceChange,
  refund,
  onRefundChange,
}: RefundOptionsProps) => {
  const CustomRadio = () => (
    <Radio value="custom">
      <Space align="center">
        <Text variant="mini">Refund</Text>
        <CurrencyInput
          currency={currency}
          value={refund}
          disabled={refundChoice !== 'custom'}
          onChange={value => onRefundChange(value as number | null)}
        />
      </Space>
    </Radio>
  )

  return (
    <Radio.Group
      value={refundChoice}
      onChange={event => onRefundChoiceChange(event.target.value)}
    >
      <Space direction="vertical">
        <Radio value="none">Refund nothing</Radio>
        <Radio value="prorate">Refund the unused part of the period</Radio>
        <CustomRadio></CustomRadio>
      </Space>
    </Radio.Group>
  )
}
type Props = {
  user_id: string
  subscription: UserSubscription
}

const SubscriptionActions = ({ user_id, subscription }: Props) => {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [refundChoice, setRefundChoice] = useState<RefundChoice>('none')
  const [refund, setRefund] = useState<number | null>(null)
  const refetchSubscriptions = useRefetchSubscriptions()

  const [cancelSubscription, { loading: cancelling }] =
    useCancelUserSubscriptionMutation({
      onError: err => message.error(`Something went wrong: ${err.message}`),
    })

  const onCancel = async () => {
    const { data } = await cancelSubscription({
      variables: {
        user_id,
        subscription_id: subscription.id,
        // Either the provider works the refund out or we name a figure
        prorate_refund: refundChoice === 'prorate',
        refund_amount:
          refundChoice === 'custom' && refund !== null
            ? Math.round(refund * 100)
            : null,
      },
    })
    const cancelled = data?.cancel_user_subscription
    if (!cancelled) {
      // A mutation can resolve with an empty payload and no error, which would
      // otherwise leave the button looking like it did nothing
      message.warning('Nothing came back from the API, so nothing has changed')
      await refetchSubscriptions()
      return
    }
    message.info(
      cancelled.cancellation_refund
        ? `Subscription cancelled, refunding ${cancelled.cancellation_refund.formatted_amount}`
        : 'Subscription cancelled with no refund',
    )
    setCancelOpen(false)
    setRefundChoice('none')
    setRefund(null)
    await refetchSubscriptions()
  }

  if (!isLive(subscription)) {
    return null
  }

  const currency = subscription.scheme_product?.scheme_accounting.currency

  return (
    <>
      <Button type="link" danger onClick={() => setCancelOpen(true)}>
        Cancel now
      </Button>
      <Modal
        title="Cancel subscription now"
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        onOk={onCancel}
        okText="Cancel subscription"
        okButtonProps={{
          danger: true,
          disabled: refundChoice === 'custom' && !refund,
        }}
        confirmLoading={cancelling}
        destroyOnClose
      >
        <p>
          This ends <strong>{subscription.scheme_product?.name}</strong>{' '}
          immediately. You can refund the whole amount, a custom amount, or
          prorated amount that&apos;s not used.
        </p>
        <RefundOptions
          currency={currency}
          refundChoice={refundChoice}
          onRefundChoiceChange={setRefundChoice}
          refund={refund}
          onRefundChange={setRefund}
        />
      </Modal>
    </>
  )
}

export default SubscriptionActions
